// hooks/useTrajeRental.js
import { useState, useEffect, useCallback, useRef } from 'react';

export const useTrajeRental = () => {
  // Estados principales
  const [trajes, setTrajes] = useState([]);
  const [categorias, setCategorias] = useState(['Todos']);
  const [filtroCategoria, setFiltroCategoria] = useState('Todos');
  const [modalTallas, setModalTallas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Cache para imágenes
  const imageCache = useRef(new Map());
  const [cachedImages, setCachedImages] = useState(new Set());

  //const API_BASE_URL = 'http://localhost:3001/api';
  const API_BASE_URL = 'https://traje-rental-system-0yx4.onrender.com/api';

  // Función para crear una clave única para el cache de imágenes
  const getImageCacheKey = (traje) => {
    return `${traje.id}-${traje.imagen || 'default'}`;
  };

  // Función para cachear imagen en memoria
  const cacheImage = useCallback((url, cacheKey) => {
    return new Promise((resolve, reject) => {
      if (imageCache.current.has(cacheKey)) {
        console.log(`📷 Imagen del cache: ${cacheKey}`);
        resolve(imageCache.current.get(cacheKey));
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log(`📷 Imagen cacheada: ${cacheKey}`);
        imageCache.current.set(cacheKey, url);
        setCachedImages(prev => new Set([...prev, cacheKey]));
        resolve(url);
      };

      img.onerror = () => {
        console.warn(`⚠️ Error al cachear imagen: ${cacheKey}`);
        reject(new Error(`Failed to cache image: ${url}`));
      };

      img.src = url;
    });
  }, []);

  // Función para obtener URL de imagen
  const getImageUrl = useCallback((traje) => {
    const cacheKey = getImageCacheKey(traje);
    
    if (imageCache.current.has(cacheKey)) {
      return imageCache.current.get(cacheKey);
    }

    if (traje.imagen && traje.imagen.startsWith('https://')) {
      return traje.imagen;
    }
    if (traje.imagen) {
      return `https://storage.googleapis.com/traje-rental-system.firebasestorage.app/trajes/${traje.imagen}`;
    }
    
    const colors = ['1a1a1a', '2563eb', '374151', '6b7280'];
    return `https://via.placeholder.com/300x400/${colors[traje.id % colors.length]}/ffffff?text=Traje+${traje.id}`;
  }, []);

  // Función para precargar imágenes
  const preloadImages = useCallback(async (trajes) => {
    const imagePromises = trajes.slice(0, 10).map(async (traje) => {
      const cacheKey = getImageCacheKey(traje);
      const imageUrl = getImageUrl(traje);
      
      try {
        await cacheImage(imageUrl, cacheKey);
      } catch (error) {
        console.warn(`⚠️ No se pudo precargar imagen para traje ${traje.id}:`, error);
      }
    });

    try {
      await Promise.allSettled(imagePromises);
      console.log(`📷 Precarga completada para ${Math.min(trajes.length, 10)} imágenes`);
    } catch (error) {
      console.warn('⚠️ Error en precarga de imágenes:', error);
    }
  }, [cacheImage, getImageUrl]);

  // Limpiar cache de imágenes
  const cleanImageCache = useCallback(() => {
    if (imageCache.current.size > 50) {
      console.log('🧹 Limpiando cache de imágenes...');
      const entries = Array.from(imageCache.current.entries());
      const toKeep = entries.slice(-30);
      
      imageCache.current.clear();
      toKeep.forEach(([key, value]) => {
        imageCache.current.set(key, value);
      });
      
      setCachedImages(new Set(toKeep.map(([key]) => key)));
      console.log(`🧹 Cache limpiado. Mantenidas ${toKeep.length} imágenes`);
    }
  }, []);

  // Función para peticiones API
  const apiRequest = useCallback(async (endpoint, options = {}) => {
    console.log(`📡 Realizando petición a: ${API_BASE_URL}${endpoint}`);
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      console.log(`✅ Respuesta HTTP status: ${response.status}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 Datos recibidos:', data);
      return data;
    } catch (error) {
      console.error('❌ Error en apiRequest:', error);
      throw error;
    }
  }, []);

  // Cargar trajes
  const cargarTrajes = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando trajes con filtro:', filtroCategoria);

      const endpoint = filtroCategoria === 'Todos' 
        ? '/trajes' 
        : `/trajes?categoria=${encodeURIComponent(filtroCategoria)}`;
      
      const response = await apiRequest(endpoint);

      if (!response || !Array.isArray(response.data)) {
        console.error('❌ Respuesta inesperada del backend:', response);
        throw new Error(`Formato de respuesta inválido en /trajes. Respuesta: ${JSON.stringify(response)}`);
      }

      setTrajes(response.data);
      setLastUpdate(new Date());
      console.log('👗 Trajes cargados:', response.data);
      setError(null);

      if (response.data.length > 0) {
        setTimeout(() => {
          preloadImages(response.data);
          cleanImageCache();
        }, 100);
      }
    } catch (error) {
      console.error('❌ Error cargando trajes:', error);
      setError(error.message || 'Error al cargar los trajes');
    } finally {
      setLoading(false);
    }
  }, [filtroCategoria, apiRequest, preloadImages, cleanImageCache]);

  // Cargar categorías
  const cargarCategorias = useCallback(async () => {
    try {
      console.log('📁 Cargando categorías...');
      const response = await apiRequest('/categorias');
      if (!response || !Array.isArray(response.data)) {
        throw new Error('Formato de respuesta inválido en /categorias');
      }
      const categoriasUnicas = [...new Set(['Todos', ...response.data])];
      setCategorias(categoriasUnicas);
      console.log('📂 Categorías cargadas:', categoriasUnicas);
    } catch (error) {
      console.error('❌ Error cargando categorías:', error);
    }
  }, [apiRequest]);

  // Manejo de errores de imagen
  const handleImageError = (e, traje) => {
    console.warn(`⚠️ Error cargando imagen del traje ${traje.id}:`, traje.imagen);
    const colors = ['1a1a1a', '2563eb', '374151', '6b7280'];
    const fallbackUrl = `https://via.placeholder.com/300x400/${colors[traje.id % colors.length]}/ffffff?text=Traje+${traje.id}`;
    
    const cacheKey = getImageCacheKey(traje);
    imageCache.current.set(cacheKey, fallbackUrl);
    
    e.target.src = fallbackUrl;
  };

  // Manejo de carga de imagen
  const handleImageLoad = useCallback((traje) => {
    const cacheKey = getImageCacheKey(traje);
    const imageUrl = getImageUrl(traje);
    
    if (!imageCache.current.has(cacheKey)) {
      cacheImage(imageUrl, cacheKey).catch(error => {
        console.warn(`⚠️ Error al cachear imagen tras carga: ${error.message}`);
      });
    }
  }, [cacheImage, getImageUrl]);

  // Detectar cambios en la conectividad
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Conexión restaurada');
      setIsOnline(true);
    };

    const handleOffline = () => {
      console.log('🚫 Conexión perdida');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    cargarCategorias();
  }, [cargarCategorias]);

  useEffect(() => {
    cargarTrajes();
  }, [cargarTrajes]);

  // Funciones de utilidad
  const mostrarTallas = (traje) => {
    console.log('👕 Mostrando tallas para traje:', traje);
    setModalTallas(traje);
  };

  const enviarWhatsApp = (traje, talla) => {
    const numeroWhatsApp = '51922521099';
    const mensaje = `Hola! Quiero reservar el traje "${traje.nombre}" en talla ${talla}. Precio: S/ ${traje.precio}`;
    const urlWhatsApp = `https://wa.me/${numeroWhatsApp}?text=${encodeURIComponent(mensaje)}`;
    console.log(`📱 Enviando mensaje WhatsApp: ${urlWhatsApp}`);
    window.open(urlWhatsApp, '_blank');
  };

  const formatLastUpdate = () => {
    if (!lastUpdate) return 'Nunca';
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);
    
    if (diff < 60) return `hace ${diff}s`;
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
    return `hace ${Math.floor(diff / 3600)}h`;
  };

  // Validación de datos
  const trajesValidos = trajes.filter(traje => 
    traje && 
    traje.id && 
    traje.nombre && 
    traje.precio && 
    traje.tallas && 
    typeof traje.tallas === 'object'
  );

  return {
    // Estados
    trajes: trajesValidos,
    categorias,
    filtroCategoria,
    modalTallas,
    loading,
    error,
    isOnline,
    lastUpdate,
    cachedImages,

    // Funciones
    setFiltroCategoria,
    setModalTallas,
    cargarTrajes,
    mostrarTallas,
    enviarWhatsApp,
    formatLastUpdate,
    getImageUrl,
    handleImageError,
    handleImageLoad
  };
};